// Yoga Wells - SEO-optimized location page
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car } from "lucide-react";
import Navigation from "../../components/shared/Navigation";
import Footer from "../../components/shared/Footer";
import PageBreadcrumb from "../../components/shared/PageBreadcrumb";
import { RetreatPromoBanner } from "../../components/shared/RetreatPromo";
import { usePageMeta, generateLocationSchema, generateFAQSchema, SchemaScript } from "../../components/shared/SEOHelper";

const YogaWells = () => {
  usePageMeta({
    title: "Yoga Classes Wells | Annelie Lily",
    description: "Yoga classes for Wells, Somerset. Small group Hatha & Yin yoga in Frome, 25 minutes from Wells. British Wheel of Yoga teacher, all levels welcome.",
    canonical: "https://annelielily.com/areas/yoga-wells"
  });

  const locationSchema = generateLocationSchema({
    townName: "Wells",
    description: "Yoga classes for Wells at Annelie Lily Yoga in Frome. Small group classes and private sessions 25 minutes from Wells Cathedral.",
    url: "https://annelielily.com/yoga-wells"
  });

  const faqs = [
    {
      question: "What's the best route from Wells to Frome?",
      answer: "Take the A39 towards Shepton Mallet, then pick up the A361 south to Frome. It's about 25 minutes through the Somerset countryside — quite a scenic drive."
    },
    {
      question: "Are your classes suitable for older adults?",
      answer: "Yes. I teach students of all ages and adapt the practice accordingly. Yoga can be made accessible for most bodies — we just need to work with what you have, not against it."
    },
    {
      question: "What should I bring to my first class?",
      answer: "Comfortable clothing, a water bottle, and perhaps a blanket or extra layer for relaxation. I have yoga mats available if you don't have your own yet."
    },
    {
      question: "How often should I practise?",
      answer: "Once a week is a good starting point — enough to feel the benefits and build consistency. Some students build up to more as yoga becomes part of their routine."
    },
    {
      question: "Do you offer anything besides yoga classes?",
      answer: "Yes. I also offer holistic treatments including massage, Reiki, and chakra healing, as well as monthly women's circles and occasional day retreats."
    }
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={locationSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Yoga in Wells" currentSlug="areas/yoga-wells" />
      
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">Yoga for Wells</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">Yoga Classes Wells</h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">
              A scenic 25-minute drive through the Mendips brings you to Frome. 
              Small, welcoming yoga classes with genuine space for individual attention.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><MapPin className="w-4 h-4 text-gold" />Based in Frome</span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><Car className="w-4 h-4 text-gold" />25 min from Wells</span>
            </div>
            <Link to="/contact" className="btn-primary">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
          </motion.div>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">Yoga for Wells Residents</h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p>
              Wells is a beautiful place to live, but sometimes the smaller towns don't have 
              the yoga options you're looking for. Frome offers something different — intimate 
              classes where you're not just a face in the crowd.
            </p>
            <p>
              The drive from Wells takes you through some of Somerset's loveliest countryside. 
              Many students tell me they arrive already feeling more relaxed — the journey becomes 
              part of the practice.
            </p>
            <p>
              My <Link to="/yoga-classes" className="text-gold hover:underline">yoga classes</Link> combine 
              traditional <Link to="/areas/yoga-wells/hatha-yoga" className="text-gold hover:underline">Hatha yoga near Wells</Link> with 
              the slower, deeper work of <Link to="/areas/yoga-wells/yin-yoga" className="text-gold hover:underline">Yin yoga</Link>. 
              If you're <Link to="/yoga-classes/beginner-yoga" className="text-gold hover:underline">new to yoga</Link>, 
              you'll find a supportive environment where questions are welcome and nobody's judging.
            </p>
            <p>
              The main yoga styles I teach are <Link to="/yoga-classes/hatha-yoga" className="text-gold hover:underline">Hatha Yoga</Link> (active, strength-building) and <Link to="/yoga-classes/yin-yoga" className="text-gold hover:underline">Yin Yoga</Link> (slower, deeply restorative).
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">Yoga Classes for Wells</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Hatha Yoga Wells", desc: "Traditional practice with postures, breathing, and relaxation. Builds strength and flexibility gradually.", link: "/areas/yoga-wells/hatha-yoga" },
              { title: "Yin Yoga Wells", desc: "Slow, meditative practice with long-held stretches. Wonderful for releasing deep-held tension.", link: "/areas/yoga-wells/yin-yoga" },
              { title: "Private Sessions Wells", desc: "One-to-one yoga designed around your specific needs and goals.", link: "/areas/yoga-wells/private-yoga" },
            ].map((service) => (
              <Link key={service.title} to={service.link} className="block bg-white/70 p-6 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group">
                <h3 className="font-heading text-xl text-neutral-800 mb-2 group-hover:text-gold">{service.title}</h3>
                <p className="text-neutral-600 text-sm mb-3">{service.desc}</p>
                <span className="text-gold text-sm font-medium">Learn more →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
            Questions from Wells Students
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                <p className="text-neutral-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">Ready to Try a Class?</h2>
          <p className="text-neutral-600 mb-6">Get in touch and I'll help you find the right class.</p>
          <Link to="/contact" className="btn-primary">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
        </div>
      </section>

      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Also serving:</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: "Bath", link: "/areas/yoga-bath" },
              { name: "Trowbridge", link: "/areas/yoga-trowbridge" },
              { name: "Warminster", link: "/areas/yoga-warminster" },
              { name: "Westbury", link: "/areas/yoga-westbury" },
              { name: "Shepton Mallet", link: "/areas/yoga-shepton-mallet" },
              { name: "Radstock", link: "/areas/yoga-radstock" },
            ].map((area) => (
              <Link key={area.name} to={area.link} className="text-sm text-gold hover:underline">{area.name}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* Retreat Promotion */}
      <RetreatPromoBanner variant="highlight" />

      <Footer />
    </div>
  );
};

export default YogaWells;
